/**
 * Inspection & Test Plan (ITP) — GAP 1
 *
 * Define the inspection regime for a work package BEFORE work starts.
 * Item types: H (Hold), W (Witness), S (Surveillance), R (Review).
 * Hold points are advisory in this version — future release will block DPR completion.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  Plus, X, Loader2, ChevronRight, AlertTriangle, CheckCircle2,
  FileCheck, ClipboardList, Info,
} from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import { apiGet, apiPost, apiPut, apiPatch } from '../../lib/api';

const STATUS_BADGE = {
  Draft:      'bg-slate-100 text-slate-600 border-slate-200',
  Submitted:  'bg-blue-50 text-blue-700 border-blue-200',
  Approved:   'bg-emerald-50 text-emerald-700 border-emerald-200',
  Superseded: 'bg-slate-100 text-slate-400 border-slate-200',
};

const TYPE_BADGE = {
  H: 'bg-rose-100 text-rose-700 border-rose-200',
  W: 'bg-amber-50 text-amber-700 border-amber-200',
  S: 'bg-blue-50 text-blue-700 border-blue-200',
  R: 'bg-slate-100 text-slate-600 border-slate-200',
};

const TYPE_LABELS = {
  H: 'Hold', W: 'Witness', S: 'Surveillance', R: 'Review',
};

export default function ITPList() {
  const [itps, setItps] = useState([]);
  const [projects, setProjects] = useState([]);
  const [meta, setMeta] = useState({ disciplines: [], inspection_types: [], responsible_parties: [] });
  const [filterProject, setFilterProject] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterProject) params.project_id = filterProject;
      if (filterStatus) params.status = filterStatus;
      const d = await apiGet('/quality/itps', params);
      setItps(d.items || []);
    } catch { toast.error('Failed to load ITPs'); }
    finally { setLoading(false); }
  }, [filterProject, filterStatus]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    apiGet('/projects').then((r) => setProjects(Array.isArray(r) ? r : (r?.projects || []))).catch(() => {});
    apiGet('/quality/itp-disciplines').then(setMeta).catch(() => {});
  }, []);

  const submitItp = async (itp) => {
    try {
      await apiPatch(`/quality/itps/${itp.id}/submit`);
      toast.success('ITP submitted for approval');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const approveItp = async (itp) => {
    try {
      await apiPatch(`/quality/itps/${itp.id}/approve`);
      toast.success('ITP approved');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const openHolds = (itp) => (itp.items || []).filter((it) => it.inspection_type === 'H' && it.sign_off_status !== 'signed').length;

  return (
    <div className="space-y-5">
      <PageHeader overline="Quality" title="Inspection & Test Plans (ITP)"
        subtitle="Define inspection regimes per discipline before work starts. H = Hold point, W = Witness, S = Surveillance, R = Review." />

      {/* Advisory note */}
      <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-sm px-4 py-3 text-xs text-amber-800">
        <Info size={14} className="shrink-0 mt-0.5" />
        <div>
          <strong>Advisory mode:</strong> Hold points currently show a warning but do not block DPR activity completion.
          Hard blocking will be enforced in a future release. Ensure Hold-point inspections are signed off before
          marking activities complete.
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <select value={filterProject} onChange={(e) => setFilterProject(e.target.value)}
          className="px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white min-w-[220px]"
          data-testid="itp-filter-project">
          <option value="">All projects</option>
          {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
          className="px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white"
          data-testid="itp-filter-status">
          <option value="">All statuses</option>
          {['Draft', 'Submitted', 'Approved'].map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <button onClick={() => setCreating(true)}
          className="ml-auto flex items-center gap-1.5 px-3 py-2 text-xs bg-[#3b4872] text-white rounded-sm font-display font-bold hover:bg-[#2c3658]"
          data-testid="itp-new-btn">
          <Plus size={13} /> New ITP
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 className="animate-spin mr-2" />Loading…</div>
      ) : itps.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-sm p-12 text-center text-slate-400 text-sm">
          No ITPs yet. Create an ITP for each work discipline before construction starts.
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
          <table className="w-full dense-table text-xs" data-testid="itp-table">
            <thead><tr>
              <th>Title</th><th>Project</th><th>Discipline</th><th>Revision</th>
              <th className="text-center">Items</th><th className="text-center">Open Holds</th>
              <th>Status</th><th>Approved By</th><th></th>
            </tr></thead>
            <tbody>
              {itps.map((itp) => {
                const holds = openHolds(itp);
                return (
                  <tr key={itp.id} className="hover:bg-slate-50" data-testid={`itp-row-${itp.id}`}>
                    <td className="font-semibold">{itp.title}</td>
                    <td className="truncate max-w-[140px]">{itp.project_id}</td>
                    <td>{itp.discipline}</td>
                    <td className="font-mono">{itp.revision}</td>
                    <td className="text-center">{(itp.items || []).length}</td>
                    <td className="text-center">
                      {holds > 0
                        ? <span className="inline-flex items-center gap-1 text-rose-600 font-bold"><AlertTriangle size={11} />{holds}</span>
                        : <CheckCircle2 size={13} className="text-emerald-500 mx-auto" />}
                    </td>
                    <td><span className={`px-1.5 py-0.5 text-[10px] border rounded-sm font-display font-bold uppercase ${STATUS_BADGE[itp.status] || ''}`}>{itp.status}</span></td>
                    <td className="text-slate-500">{itp.approved_by_client || '—'}</td>
                    <td>
                      <div className="flex items-center gap-1">
                        {itp.status === 'Draft' && (
                          <button onClick={() => submitItp(itp)}
                            className="px-2 py-1 text-[10px] bg-blue-600 text-white rounded-sm font-display font-bold"
                            data-testid={`itp-submit-${itp.id}`}>Submit</button>
                        )}
                        {itp.status === 'Submitted' && (
                          <button onClick={() => approveItp(itp)}
                            className="px-2 py-1 text-[10px] bg-emerald-600 text-white rounded-sm font-display font-bold"
                            data-testid={`itp-approve-${itp.id}`}>Approve</button>
                        )}
                        <button onClick={() => setEditing(itp)}
                          className="px-2 py-1 text-[10px] border border-slate-300 text-slate-600 rounded-sm font-display font-bold"
                          data-testid={`itp-view-${itp.id}`}>
                          <ChevronRight size={11} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {creating && (
        <ITPDrawer projects={projects} meta={meta} mode="create"
          onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />
      )}
      {editing && (
        <ITPDrawer projects={projects} meta={meta} mode="view" itp={editing}
          onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}


function ITPDrawer({ projects, meta, mode, itp, onClose, onSaved }) {
  const isCreate = mode === 'create';
  const [form, setForm] = useState({
    project_id: itp?.project_id || '',
    discipline: itp?.discipline || '',
    title: itp?.title || '',
    revision: itp?.revision || 'Rev 0',
    notes: itp?.notes || '',
    items: itp?.items || [],
  });
  const [saving, setSaving] = useState(false);
  const [coverage, setCoverage] = useState({});   // by_item_seq keyed by seq
  const canEdit = isCreate || itp?.status === 'Draft';

  // Load WIR coverage when viewing an existing ITP
  useEffect(() => {
    if (!isCreate && itp?.id) {
      import('../../lib/api').then(({ apiGet }) => {
        apiGet(`/quality/itps/${itp.id}/linked-inspections`)
          .then((d) => setCoverage(d.by_item_seq || {}))
          .catch(() => {});
      });
    }
  }, [isCreate, itp?.id]);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const addItem = () => setForm((f) => ({
    ...f,
    items: [...f.items, { seq: f.items.length + 1, activity: '', stage: '', acceptance_criteria: '', reference_standard: '', inspection_type: 'W', responsible: ['Contractor'], record_format: '', sign_off_status: 'pending' }],
  }));

  const updItem = (i, k, v) => setForm((f) => ({
    ...f,
    items: f.items.map((it, idx) => idx === i ? { ...it, [k]: v } : it),
  }));

  const removeItem = (i) => setForm((f) => ({
    ...f,
    items: f.items.filter((_, idx) => idx !== i).map((it, idx) => ({ ...it, seq: idx + 1 })),
  }));

  const save = async () => {
    if (!form.project_id || !form.discipline || !form.title)
      return toast.error('Fill project, discipline, and title');
    setSaving(true);
    try {
      if (isCreate) {
        await apiPost('/quality/itps', form);
        toast.success('ITP created');
      } else {
        await apiPut(`/quality/itps/${itp.id}`, form);
        toast.success('ITP updated');
      }
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-3xl my-4">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-4 flex items-center justify-between z-10">
          <div className="font-display font-bold text-base">
            {isCreate ? 'New Inspection & Test Plan' : form.title}
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>

        <div className="px-5 py-4 space-y-4 max-h-[70vh] overflow-y-auto">
          {/* Header fields */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Project *</label>
              <select value={form.project_id} onChange={upd('project_id')} disabled={!canEdit}
                className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-[#3b4872] disabled:bg-slate-50">
                <option value="">— Select —</option>
                {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Discipline *</label>
              <select value={form.discipline} onChange={upd('discipline')} disabled={!canEdit}
                className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-[#3b4872] disabled:bg-slate-50">
                <option value="">— Select —</option>
                {(meta.disciplines || []).map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Title *</label>
              <input value={form.title} onChange={upd('title')} disabled={!canEdit}
                placeholder="e.g. RCC Works ITP"
                className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-[#3b4872] disabled:bg-slate-50" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Revision</label>
              <input value={form.revision} onChange={upd('revision')} disabled={!canEdit}
                placeholder="Rev 0"
                className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-[#3b4872] disabled:bg-slate-50" />
            </div>
          </div>

          {/* Items table */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-semibold text-slate-700">Inspection Points ({form.items.length})</div>
              {canEdit && (
                <button onClick={addItem}
                  className="flex items-center gap-1 text-xs text-[#3b4872] font-bold"
                  data-testid="itp-add-item">
                  <Plus size={12} /> Add Point
                </button>
              )}
            </div>

            {/* Legend */}
            <div className="flex items-center gap-3 mb-2">
              {Object.entries(TYPE_LABELS).map(([k, v]) => (
                <span key={k} className={`px-1.5 py-0.5 text-[9px] border rounded font-display font-bold ${TYPE_BADGE[k]}`}>
                  {k} — {v}
                </span>
              ))}
            </div>

            <div className="border border-slate-200 rounded overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-2 py-2 text-left w-8">#</th>
                    <th className="px-2 py-2 text-left">Activity</th>
                    <th className="px-2 py-2 text-left w-16">Type</th>
                    <th className="px-2 py-2 text-left">Acceptance Criteria</th>
                    <th className="px-2 py-2 text-left">Reference</th>
                    <th className="px-2 py-2 text-center w-20">Sign-off</th>
                    {!isCreate && <th className="px-2 py-2 text-center w-16">WIRs</th>}
                    {canEdit && <th className="w-6" />}
                  </tr>
                </thead>
                <tbody>
                  {form.items.length === 0 && (
                    <tr><td colSpan={canEdit ? 8 : 7} className="px-3 py-4 text-center text-slate-400">No inspection points yet. Click "Add Point".</td></tr>
                  )}
                  {form.items.map((it, i) => {
                    const wirs = coverage[it.seq] || [];
                    const passedWirs = wirs.filter((w) => ['passed','passed_with_observations'].includes(w.status));
                    return (
                    <tr key={i} className="border-b border-slate-100">
                      <td className="px-2 py-1.5 text-slate-400">{it.seq}</td>
                      <td className="px-2 py-1.5">
                        {canEdit
                          ? <input value={it.activity} onChange={(e) => updItem(i, 'activity', e.target.value)}
                              placeholder="Activity / stage"
                              className="w-full border-0 outline-none bg-transparent text-xs placeholder:text-slate-300" />
                          : it.activity}
                      </td>
                      <td className="px-2 py-1.5">
                        {canEdit
                          ? <select value={it.inspection_type} onChange={(e) => updItem(i, 'inspection_type', e.target.value)}
                              className="border-0 outline-none bg-transparent text-xs">
                              {['H', 'W', 'S', 'R'].map((t) => <option key={t} value={t}>{t}</option>)}
                            </select>
                          : <span className={`px-1.5 py-0.5 text-[9px] border rounded font-display font-bold ${TYPE_BADGE[it.inspection_type]}`}>
                              {it.inspection_type}
                            </span>}
                      </td>
                      <td className="px-2 py-1.5">
                        {canEdit
                          ? <input value={it.acceptance_criteria || ''} onChange={(e) => updItem(i, 'acceptance_criteria', e.target.value)}
                              placeholder="IS code / spec clause"
                              className="w-full border-0 outline-none bg-transparent text-xs placeholder:text-slate-300" />
                          : <span className="text-slate-500">{it.acceptance_criteria || '—'}</span>}
                      </td>
                      <td className="px-2 py-1.5">
                        {canEdit
                          ? <input value={it.reference_standard || ''} onChange={(e) => updItem(i, 'reference_standard', e.target.value)}
                              placeholder="IS 456, Drawing B-101"
                              className="w-full border-0 outline-none bg-transparent text-xs placeholder:text-slate-300" />
                          : <span className="text-slate-500">{it.reference_standard || '—'}</span>}
                      </td>
                      <td className="px-2 py-1.5 text-center">
                        {it.sign_off_status === 'signed'
                          ? <span className="text-emerald-600 flex items-center justify-center gap-1 text-[10px]">
                              <CheckCircle2 size={11} />
                              {it.signed_via_wir_id ? 'via WIR' : 'Signed'}
                            </span>
                          : it.inspection_type === 'H'
                            ? <span className="text-rose-500 text-[10px] font-bold">PENDING</span>
                            : <span className="text-slate-300 text-[10px]">—</span>}
                      </td>
                      {!isCreate && (
                        <td className="px-2 py-1.5 text-center text-[10px]">
                          {wirs.length > 0
                            ? <span className={`font-bold ${passedWirs.length > 0 ? 'text-emerald-600' : 'text-amber-600'}`}
                                title={wirs.map(w => `${w.status} — ${w.completed_at?.slice(0,10) || w.raised_at?.slice(0,10)}`).join('\n')}>
                                {passedWirs.length}/{wirs.length}
                              </span>
                            : <span className="text-slate-300">—</span>}
                        </td>
                      )}
                      {canEdit && (
                        <td className="px-1 py-1.5">
                          <button onClick={() => removeItem(i)} className="text-slate-300 hover:text-rose-500"><X size={12} /></button>
                        </td>
                      )}
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {form.notes !== undefined && (
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Notes</label>
              <textarea value={form.notes} onChange={upd('notes')} disabled={!canEdit} rows={2}
                className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-[#3b4872] disabled:bg-slate-50 resize-none" />
            </div>
          )}
        </div>

        {canEdit && (
          <div className="sticky bottom-0 bg-white border-t border-slate-200 px-5 py-3 flex gap-2">
            <button onClick={save} disabled={saving}
              className="flex-1 bg-[#3b4872] text-white py-2.5 rounded-sm font-display font-bold text-sm disabled:opacity-50"
              data-testid="itp-save-btn">
              {saving ? <Loader2 size={14} className="animate-spin mx-auto" /> : isCreate ? 'Create ITP' : 'Save Changes'}
            </button>
            <button onClick={onClose} className="px-4 py-2.5 border border-slate-300 rounded-sm text-sm text-slate-600">Cancel</button>
          </div>
        )}
        {!canEdit && (
          <div className="sticky bottom-0 bg-white border-t border-slate-200 px-5 py-3">
            <button onClick={onClose} className="w-full py-2.5 border border-slate-300 rounded-sm text-sm text-slate-600">Close</button>
          </div>
        )}
      </div>
    </div>
  );
}
